import { baseApi } from '@app/store/index.api';

import {
  IRoutingConfigDraftCreateBody,
  IRoutingConfigMetadataQuery,
  IRoutingConfigPublishQuery,
  IRoutingConfigUpdateQuery,
  IRoutingConfigVersionResponse,
  IRoutingConfigVersionsResponse,
} from '../model/types';

// Exported for its cache utils: Save Draft upserts a PATCH response into the by-id
// cache instead of invalidating (see patchRoutingConfigVersionMetadata below).
export const routingConfigVersionApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getRoutingConfigVersions: builder.query<IRoutingConfigVersionsResponse, string | void>({
      query: query => ({
        url: query ? `/routing/config/versions?${query}` : '/routing/config/versions',
      }),
      providesTags: ['RoutingConfigVersions'],
    }),
    getActiveRoutingConfigVersion: builder.query<IRoutingConfigVersionResponse, void>({
      query: () => ({
        url: '/routing/config/versions/active',
      }),
      providesTags: ['RoutingConfigVersions'],
    }),
    getRoutingConfigVersion: builder.query<IRoutingConfigVersionResponse, number>({
      query: id => ({
        url: `/routing/config/versions/${id}`,
      }),
      providesTags: ['RoutingConfigVersions'],
    }),
    // Invalidates nothing on purpose: the follow-up PUT does, and a refetch mid-save
    // would race the two-step Save Draft.
    createRoutingConfigDraft: builder.mutation<
      IRoutingConfigVersionResponse,
      IRoutingConfigDraftCreateBody
    >({
      query: body => ({
        url: '/routing/config/versions/draft',
        method: 'POST',
        body,
      }),
    }),
    // Name/comment only. Like the draft POST it invalidates nothing: Save Draft runs
    // it right before the config PUT, which invalidates once for the whole save, and
    // upserts the response into the by-id cache itself so a failed PUT still shows
    // the saved metadata.
    patchRoutingConfigVersionMetadata: builder.mutation<
      IRoutingConfigVersionResponse,
      IRoutingConfigMetadataQuery
    >({
      query: ({ id, body }) => ({
        url: `/routing/config/versions/${id}`,
        method: 'PATCH',
        body,
      }),
    }),
    updateRoutingConfigVersion: builder.mutation<
      IRoutingConfigVersionResponse,
      IRoutingConfigUpdateQuery
    >({
      query: ({ id, body }) => ({
        url: `/routing/config/versions/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['RoutingConfigVersions'],
    }),
    // Makes the draft the published version; the previous one becomes archived on
    // the backend. Invalidating refetches the viewed, active and listed versions,
    // so the status badges update without a reload.
    publishRoutingConfigVersion: builder.mutation<
      IRoutingConfigVersionResponse,
      IRoutingConfigPublishQuery
    >({
      query: ({ id, body }) => ({
        url: `/routing/config/versions/${id}/publish`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RoutingConfigVersions'],
    }),
  }),
});

export const {
  useGetRoutingConfigVersionsQuery,
  useGetActiveRoutingConfigVersionQuery,
  useGetRoutingConfigVersionQuery,
  useCreateRoutingConfigDraftMutation,
  usePatchRoutingConfigVersionMetadataMutation,
  useUpdateRoutingConfigVersionMutation,
  usePublishRoutingConfigVersionMutation,
} = routingConfigVersionApi;
